// Host-side egress for COBT -> Brex iframe messages.
//
// INFRASTRUCTURE ONLY — nothing emits yet. This stands up the capability so a
// use case can send a message later with a one-liner, without reworking plumbing.
//
// The bridge is INERT unless it is running as the Brex app (`?isBrexApp=true`).
// Beyond that gate, egress routes by host type:
//   - Web iframe: also requires an allowed parent origin (`PARENT_FRAME_ORIGIN`
//     env). Posts via window.parent.postMessage to that exact origin.
//   - React Native WebView (Brex mobile): posts via
//     window.ReactNativeWebView.postMessage (native channel, no origin needed).
// On every tenant/standalone/non-brex context it is a no-op.
//
// When a use case arises there are two ways to feed it (no change here needed):
//   - Model 1 (host-observable): host code calls `brexBridge.emit(type, payload)`.
//   - Model 2 (bundle-internal): any code in the window dispatches a
//     `host:message` CustomEvent; the relay (initBrexBridge) forwards it.
//     That lets the b2bportal bundle reach the bridge without importing the host
//     (it would add a tiny tenant-agnostic `notifyHost` helper only when needed).
//
// All of brex's bridge logic — origin, isBrexApp gate, schema — lives here, so it
// is brex-contained and is the single controlled egress to the Brex parent.

import { logger } from "../components/Logger";

// --- config seam -----------------------------------------------------------
// Brex's embedding origin(s), from the deploy env (window.__mclean_env__). Read
// the raw env object directly: `config` (../utils/config) only surfaces a fixed
// allowlist of keys and does NOT pass PARENT_FRAME_ORIGIN through.
//
// Value is a comma-separated allowlist so a non-prod env can carry more than one
// origin (e.g. Brex's dashboard + a Brex-local dev origin for integration
// testing). EMPTY keeps the bridge inert; never post to "*".
const getParentFrameOrigins = (): string[] => {
  const env = window.__mclean_env__ as unknown as
    | Record<string, unknown>
    | undefined;
  const raw = env?.PARENT_FRAME_ORIGIN;
  if (typeof raw !== "string" || raw === "") {
    return [];
  }
  return raw
    .split(",")
    .map((origin) => origin.trim())
    .filter((origin) => origin !== "");
};

// Are we running as the Brex embedded app? Brex loads COBT with `?isBrexApp=true`.
// Captured once and persisted so it survives client-side navigation that drops
// the query param (mirrors how the session tokens are kept).
const IS_BREX_APP_KEY = "cobt-is-brex-app";
export const getIsBrexApp = (): boolean => {
  try {
    const fromUrl =
      new URLSearchParams(window.location.search).get("isBrexApp") === "true";
    if (fromUrl) {
      window.sessionStorage.setItem(IS_BREX_APP_KEY, "true");
    }
    return fromUrl || window.sessionStorage.getItem(IS_BREX_APP_KEY) === "true";
  } catch {
    return false;
  }
};

// True only when actually inside a parent frame (not a top-level tab).
const isEmbedded = (): boolean =>
  window.parent != null && window.parent !== window;

// Detect a React Native WebView host (Brex mobile). RN injects
// `window.ReactNativeWebView` whose `postMessage(string)` hands the message
// straight to the native app — there is no parent frame and no targetOrigin, so
// this path is separate from the web-iframe path below.
const getReactNativeWebView = ():
  | { postMessage: (data: string) => void }
  | undefined => {
  const rnw = (
    window as unknown as {
      ReactNativeWebView?: { postMessage?: unknown };
    }
  ).ReactNativeWebView;
  return rnw != null && typeof rnw.postMessage === "function"
    ? (rnw as { postMessage: (data: string) => void })
    : undefined;
};

// --- message contract ------------------------------------------------------
// Stable envelope; grow the type union as use cases arise (additive only — a
// breaking change should be a new type, e.g. BOOKING_COMPLETE_V2, not a rename).
export type BrexBridgeMessageType =
  | "COBT_READY"
  | "BOOKING_COMPLETE"
  | "CLOSE_REQUESTED"
  | "SEARCH_RESULTS_LOADING"
  | "PAGE_LOADING"
  | "PAGE_READY"
  | "ERROR";

export interface BrexBridgeMessage {
  source: "cobt";
  type: BrexBridgeMessageType;
  payload?: unknown;
}

// Agreed payload shape for the ERROR message (per Brex). `message` is for Brex's
// logs ONLY — must never contain PII and is never shown to users.
export type BrexErrorCode =
  | "AUTH_FAILED"
  | "FORBIDDEN"
  | "UNAVAILABLE"
  | "UNKNOWN";

export interface BrexErrorPayload {
  code: BrexErrorCode;
  retryable: boolean;
  message?: string;
}

const HOST_MESSAGE_EVENT = "host:message";

// --- egress ----------------------------------------------------------------
// Posts a message to the Brex host. No-op unless running as the Brex app. Then
// routes by host type:
//   - React Native WebView (Brex mobile): window.ReactNativeWebView.postMessage
//     with a JSON string (no parent frame, no origin to target).
//   - Web iframe: window.parent.postMessage to each configured allowed origin.
const emit = (type: BrexBridgeMessageType, payload?: unknown): void => {
  if (!getIsBrexApp()) {
    return;
  }
  const message: BrexBridgeMessage = { source: "cobt", type, payload };

  // Mobile: native WebView channel. Message goes only to the native app that
  // created the WebView, so there is no cross-origin target (hence no origin
  // allowlist here); the getIsBrexApp gate above still scopes it to Brex.
  const rnWebView = getReactNativeWebView();
  if (rnWebView != null) {
    rnWebView.postMessage(JSON.stringify(message));
    return;
  }

  // Web: must be in a parent frame with at least one allowed origin configured.
  if (!isEmbedded()) {
    return;
  }
  const targetOrigins = getParentFrameOrigins();
  if (targetOrigins.length === 0) {
    return; // inert until Brex's origin is configured
  }
  // Post once per allowed origin. The browser only DELIVERS a message to the
  // parent whose actual origin matches targetOrigin, so listing several origins
  // never leaks — posts whose targetOrigin isn't the real parent are dropped.
  for (const targetOrigin of targetOrigins) {
    window.parent.postMessage(message, targetOrigin);
  }
};

export const brexBridge = { emit };

// --- ingress (Brex -> COBT) ------------------------------------------------
// Messages the Brex host posts into this iframe. Same envelope shape as egress,
// with `source: "brex"`. To support a new message: add a union member, a payload
// validator, and subscribe a handler with `onBrexMessage` — the gate and dispatch
// below don't change. Additive only, mirroring the egress rule.
export type BrexInboundMessage = {
  type: "LAUNCH_CODE";
  // Raw Brex launch JWT (no `jwt:` prefix). Single-use, short-lived; never log or persist.
  payload: { launchCode: string };
};

export type BrexInboundMessageType = BrexInboundMessage["type"];

export type BrexInboundPayload<T extends BrexInboundMessageType> = Extract<
  BrexInboundMessage,
  { type: T }
>["payload"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const inboundPayloadValidators: {
  [T in BrexInboundMessageType]: (payload: unknown) => boolean;
} = {
  LAUNCH_CODE: (payload) =>
    isRecord(payload) &&
    typeof payload.launchCode === "string" &&
    payload.launchCode !== "",
};

const isKnownInboundType = (type: string): type is BrexInboundMessageType =>
  Object.prototype.hasOwnProperty.call(inboundPayloadValidators, type);

// True only when running as the Brex app inside a web parent frame — the only
// context in which inbound messages are accepted (React Native ingress TBD).
export const isEmbeddedBrexApp = (): boolean => getIsBrexApp() && isEmbedded();

type InboundHandler = (payload: unknown) => void;
const inboundHandlers = new Map<BrexInboundMessageType, Set<InboundHandler>>();
// Latest message per type that arrived before anything subscribed (e.g. before
// React mounted). Handed to the first subscriber, then dropped.
const pendingInbound = new Map<BrexInboundMessageType, unknown>();

type InboundEvent = Pick<MessageEvent, "data" | "origin" | "source">;

export const handleBrexInboundMessage = (event: InboundEvent): void => {
  // Gate: only the Brex parent frame, from an allowlisted origin. Anything else
  // (other scripts, extensions, HMR) is silently ignored.
  if (
    !isEmbeddedBrexApp() ||
    event.source !== window.parent ||
    !getParentFrameOrigins().includes(event.origin)
  ) {
    return;
  }
  const { data } = event;
  if (!isRecord(data) || data.source !== "brex") {
    return;
  }

  // From Brex but off-contract: drop and record the type + reason only —
  // never payload values, which may carry tokens.
  const type = typeof data.type === "string" ? data.type : undefined;
  if (type === undefined || !isKnownInboundType(type)) {
    logger()?.warn("Brex bridge: dropped inbound message", {
      type,
      reason: "unknown_type",
    });
    return;
  }
  if (!inboundPayloadValidators[type](data.payload)) {
    logger()?.warn("Brex bridge: dropped inbound message", {
      type,
      reason: "invalid_payload",
    });
    return;
  }

  const handlers = inboundHandlers.get(type);
  if (handlers == null || handlers.size === 0) {
    pendingInbound.set(type, data.payload);
    return;
  }
  handlers.forEach((handler) => handler(data.payload));
};

// Subscribe to one inbound message type. Returns an unsubscribe function.
export const onBrexMessage = <T extends BrexInboundMessageType>(
  type: T,
  handler: (payload: BrexInboundPayload<T>) => void
): (() => void) => {
  const wrapped: InboundHandler = (payload) =>
    handler(payload as BrexInboundPayload<T>);
  const handlers = inboundHandlers.get(type) ?? new Set<InboundHandler>();
  handlers.add(wrapped);
  inboundHandlers.set(type, handlers);

  if (pendingInbound.has(type)) {
    const payload = pendingInbound.get(type);
    pendingInbound.delete(type);
    wrapped(payload);
  }

  return () => {
    handlers.delete(wrapped);
  };
};

// Test-only: reset module state between specs.
export const resetBrexInboundForTests = (): void => {
  inboundHandlers.clear();
  pendingInbound.clear();
};

// --- Model 2 relay ---------------------------------------------------------
// Lets code anywhere in the window reach the bridge without importing it:
//   window.dispatchEvent(
//     new CustomEvent("host:message", { detail: { type, payload } })
//   );
// Inert until something dispatches. Idempotent — safe to call once at startup.
let relayAttached = false;
export const initBrexBridge = (): void => {
  if (relayAttached || typeof window === "undefined") {
    return;
  }
  relayAttached = true;
  // Attached at startup so it is listening before the Brex iframe's onLoad posts.
  window.addEventListener("message", handleBrexInboundMessage);
  window.addEventListener(HOST_MESSAGE_EVENT, (event: Event) => {
    const detail = (event as CustomEvent).detail as
      | { type?: BrexBridgeMessageType; payload?: unknown }
      | undefined;
    if (detail?.type == null) {
      return;
    }
    emit(detail.type, detail.payload);
  });
};
